import { ComponentProps, memo, useCallback, useMemo, useState } from 'react';
import { Editor } from '@tiptap/core';
import { TiptapEditor } from '@amzn/awscat-editor';
import { InlineTextFilter } from '../../../services/InlineTextOperations';
import { SuggestionPopover, SuggestionPopoverTarget } from './SuggestionPopover';
import { ImageAltEntitiesModal } from './ImageAltEntitiesModal';
import { SubsectionIdModal } from './SubsectionIdModal';
import inlineEditStyles from './TiptapEditorInlineEdit.module.scss';
import { useTiptapEditorEditingModeViewModel } from './TiptapEditorEditingModeViewModel';

type TiptapEditorProps = ComponentProps<typeof TiptapEditor>;

export interface TiptapEditorEditingModeProps extends Omit<
  TiptapEditorProps,
  | 'onInlineEditChangesModified'
  | 'onInlineEditChangeFocus'
  | 'onInlineCommentClick'
  | 'onEditAltEntities'
  | 'onEditSubsectionId'
  | 'onCopySubsectionAnchor'
  | 'editorRef'
  | 'enableUniqueId'
  | 'comments'
  | 'updateInlineCommentsLocation'
> {
  /** What inline edits and comments to display in this editor instance, or leave undefined to display nothing */
  readonly inlineTextFilter?: InlineTextFilter;
  /** Called when Reply reveals an inline comment in the sidebar. */
  readonly onCommentReply?: () => void;
}

/**
 * A TiptapEditor instance integrated with the app's inline-edit and
 * inline-comment subsystem: filtering, synchronization, keyboard/click
 * navigation, and acceptance/resolution of AI-suggested edits & comments.
 */
export const TiptapEditorEditingMode = memo(function TiptapEditorEditingMode(
  props: TiptapEditorEditingModeProps,
) {
  const { inlineTextFilter, onCommentReply, ...editorProps } = props;
  const [editor, setEditor] = useState<Editor | undefined>(undefined);
  const editorRefCallback = useCallback(
    (instance: Editor | null) => setEditor(instance ?? undefined),
    [],
  );
  const vm = useTiptapEditorEditingModeViewModel({
    inlineTextFilter,
    editor,
  });

  // When popover state changes, map it to a SuggestionPopoverTarget that the
  // SuggestionPopover component can render (enriching with editor instance / comment data).
  const popoverTarget = useMemo((): SuggestionPopoverTarget | undefined => {
    if (!vm.popover) return undefined;

    if (vm.popover.type === 'inline_edit' && editor) {
      return {
        type: 'doc_inline_edit',
        inlineEditId: vm.popover.inlineEditId,
        target: vm.popover.target,
        changeId: vm.popover.changeId,
        editor,
        shouldResolveInlineEdit: vm.popover.shouldResolveInlineEdit,
      };
    }

    if (vm.popover.type === 'inline_comment') {
      const { commentId, target, comment, isCommentOnDeletedText } = vm.popover;
      return { type: 'doc_inline_comment', target, commentId, comment, isCommentOnDeletedText };
    }

    return undefined;
  }, [vm.popover, editor]);

  return (
    <div className={inlineEditStyles.container}>
      <TiptapEditor
        {...editorProps}
        enableUniqueId
        comments={vm.editorComments}
        updateInlineCommentsLocation={vm.stageCommentPositions}
        onInlineCommentClick={vm.handleInlineCommentClick}
        onInlineEditChangesModified={vm.syncModifiedChanges}
        onInlineEditChangeFocus={vm.focusChange}
        onEditAltEntities={vm.openAltTextModal}
        onEditSubsectionId={vm.openSubsectionIdModal}
        onCopySubsectionAnchor={vm.copySubsectionAnchor}
        editorRef={editorRefCallback}
      />
      {popoverTarget && (
        <SuggestionPopover
          popoverTarget={popoverTarget}
          onClose={vm.hidePopover}
          isReadonly={props.editable === false}
          onCommentReply={onCommentReply}
        />
      )}
      {vm.isAltTextModalVisible && (
        <ImageAltEntitiesModal
          initialValue={vm.initialAltTextValue}
          onDismiss={vm.dismissAltTextModal}
          onSave={vm.saveAltTextValue}
        />
      )}
      {vm.isSubsectionIdModalVisible && (
        <SubsectionIdModal
          initialValue={vm.initialSubsectionIdValue}
          onDismiss={vm.dismissSubsectionIdModal}
          onSave={vm.saveSubsectionIdValue}
        />
      )}
    </div>
  );
});
